import React from 'react';

export type ReportInboxStatus = 'NEW' | 'PROCESSING' | 'REVIEWED';

export type ReportInboxRow = {
  id: string;
  candidate: string;
  simulation: string;
  overallScore: string;
  submitted: string;
  status: ReportInboxStatus;
  onOpen: () => void;
};

type ReportsInboxProps = {
  rows: ReportInboxRow[];
  onViewAll: () => void;
};

const badgeClass: Record<ReportInboxStatus, string> = {
  NEW: 'bg-[#eef2ff] text-[#4338ca]',
  PROCESSING: 'bg-[#fffbeb] text-[#b45309]',
  REVIEWED: 'bg-[#f0fdfa] text-[#0d9488]',
};

const ReportsInbox: React.FC<ReportsInboxProps> = ({ rows, onViewAll }) => {
  if (rows.length === 0) {
    return (
      <div className="rounded-b-[14px] border border-t-0 border-[#e2e5eb] bg-white px-6 py-10 text-center">
        <p className="font-display text-[20px] font-bold text-[#0f172a]">No reports yet</p>
        <p className="mt-2 text-[13px] text-[#64748b]">
          Candidate reports will appear here after simulation attempts are submitted.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-b-[14px] border border-t-0 border-[#e2e5eb] bg-white">
      <div className="overflow-x-auto">
        <table className="min-w-[860px] w-full text-left">
          <thead>
            <tr className="bg-[#fafdf9]">
              {['Candidate', 'Simulation', 'Overall Score', 'Submitted', 'Status', 'Action'].map((header) => (
                <th
                  key={header}
                  className="border-b-2 border-[#0d9488] px-4 py-3 text-[11px] font-bold uppercase tracking-[0.08em] text-[#475569]"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-b border-[#f0f0ee] transition-colors last:border-b-0 hover:bg-[#fafdf9]">
                <td className="px-4 py-3 text-[14px] font-semibold text-[#0f172a]">{row.candidate}</td>
                <td className="px-4 py-3 text-[13px] text-[#475569]">{row.simulation}</td>
                <td className="px-4 py-3 font-mono text-[13px] text-[#0f172a]">{row.overallScore}</td>
                <td className="px-4 py-3 text-[13px] text-[#475569]">{row.submitted}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${badgeClass[row.status]}`}>
                    {row.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    disabled={row.status === 'PROCESSING'}
                    onClick={row.onOpen}
                    className="rounded-[10px] border border-[#e2e5eb] bg-white px-3.5 py-2 text-[12px] font-semibold text-[#475569] transition-colors hover:border-[#0d9488] hover:text-[#0d9488] disabled:cursor-not-allowed disabled:border-[#e5e7eb] disabled:text-[#94a3b8]"
                  >
                    Open Report
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="border-t border-[#f0f0ee] px-4 py-3">
        <button
          type="button"
          onClick={onViewAll}
          className="text-[13px] font-semibold text-[#0d9488] transition-colors hover:text-[#0f766e]"
        >
          View all reports →
        </button>
      </div>
    </div>
  );
};

export default ReportsInbox;

